import React, {
  memo,
  useMemo,
  useCallback,
  useState,
  useRef,
  useEffect,
} from "react";
import { useTranslation } from "react-i18next";
import { UserData } from "../../api/auth";
import { editUser, setAvatar, deleteSelf, EditUserData } from "../../api/users";
import { STATIC_URL, UserStatus } from "../../constants";
import "../styles/UserPanel.css";
import { startVkAuth, isVkConfigured } from "../../platform/social/vk";
import { IS_TELEGRAM } from "../../platform";
import PhoneVerificationField, {
  PhoneVerificationValue,
} from "../auth/PhoneVerificationField";
import { formatRussianPhone, validateRussianPhone } from "../../utils/validation";

interface UserPanelProps {
  user: UserData | null;
  onClose: () => void;
  onLogout: () => void;
  onUserUpdate: (user: UserData) => void;
}

const LinkedCheck: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const UserPanel: React.FC<UserPanelProps> = memo(
  ({ user, onClose, onLogout, onUserUpdate }) => {
    const { t } = useTranslation();
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showPhoneEditor, setShowPhoneEditor] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState("");
    const fileInputRef = useRef<HTMLInputElement>(null);
    const isGuest = user?.status === UserStatus.guest;
    const [phoneVerification, setPhoneVerification] =
      useState<PhoneVerificationValue>({
        phone: user?.phone || "+7",
        verificationToken: null,
      });

    // Form state
    const [formData, setFormData] = useState({
      nickname: user?.nickname || "",
      fullname: user?.fullname || "",
      email: user?.email || "",
      password: "",
      confirmPassword: "",
      subscribed: user?.subscribed || false,
    });

    // Reset form when user changes or edit mode toggles
    useEffect(() => {
      if (user) {
        setFormData({
          nickname: user.nickname,
          fullname: user.fullname || "",
          email: user.email,
          password: "",
          confirmPassword: "",
          subscribed: user.subscribed || false,
        });
        setPhoneVerification({
          phone: user.phone || "+7",
          verificationToken: null,
        });
        setShowPhoneEditor(false);
      }
    }, [user, isEditing]);

    const avatarUrl = useMemo(() => {
      if (!user?.avatar) return null;

      if (
        user.avatar.startsWith("http://") ||
        user.avatar.startsWith("https://")
      ) {
        return user.avatar;
      }

      return `${STATIC_URL}${user.avatar.replace(/^\//, "")}`;
    }, [user?.avatar]);

    const userInitial = useMemo(
      () => user?.nickname?.charAt(0).toUpperCase() || "U",
      [user?.nickname]
    );

    const handleOverlayClick = useCallback(
      (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      },
      [onClose]
    );

    const handlePanelClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
    }, []);

    const isDataChanged = useMemo(() => {
      if (!user) return false;

      const dataToCompare = isGuest || showPassword
        ? formData
        : { ...formData, password: "", confirmPassword: "" };

      return (
        dataToCompare.nickname !== user.nickname ||
        dataToCompare.fullname !== (user.fullname || "") ||
        dataToCompare.email !== user.email ||
        dataToCompare.subscribed !== (user.subscribed || false) ||
        ((isGuest || showPassword) && dataToCompare.password !== "") ||
        !!phoneVerification.verificationToken ||
        (showPhoneEditor && phoneVerification.phone !== (user.phone || "+7"))
      );
    }, [
      formData,
      user,
      showPassword,
      showPhoneEditor,
      isGuest,
      phoneVerification.phone,
      phoneVerification.verificationToken,
    ]);

    const handleInputChange = useCallback(
      (field: keyof typeof formData, value: string | boolean) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
      },
      []
    );

    const handleAvatarClick = useCallback(() => {
      if (isEditing) {
        fileInputRef.current?.click();
      }
    }, [isEditing]);

    const handleAvatarChange = useCallback(
      async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith("image/")) {
          alert(t("user.selectImageFile"));
          return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          alert(t("user.imageSizeLimit"));
          return;
        }

        try {
          setIsLoading(true);
          const updatedUser = await setAvatar(file);
          onUserUpdate(updatedUser);
        } catch (error: any) {
          alert(error.message || t("errors.failedToUploadAvatar"));
        } finally {
          setIsLoading(false);
        }
      },
      [onUserUpdate, t]
    );

    const handleSubmit = useCallback(async () => {
      if (!isDataChanged || isLoading) return;

      // A guest becomes a regular account on save, so the server requires a
      // password. Existing users may still leave the optional password change blank.
      if (isGuest || (showPassword && formData.password)) {
        if (formData.password !== formData.confirmPassword) {
          alert(t("auth.passwordsDoNotMatch"));
          return;
        }
        if (formData.password.length < 6) {
          alert(t("auth.passwordMinLength"));
          return;
        }
      }

      // Validate email
      if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
        alert(t("auth.invalidEmail"));
        return;
      }

      // Validate nickname
      if (!formData.nickname || formData.nickname.length < 3) {
        alert(t("auth.nicknameMinLength"));
        return;
      }

      const hasEnteredPhone = phoneVerification.phone !== "+7";
      const phoneHasChanged =
        phoneVerification.phone !== (user?.phone || "+7");
      if (
        (isGuest || (hasEnteredPhone && phoneHasChanged)) &&
        (!phoneVerification.verificationToken ||
          !validateRussianPhone(phoneVerification.phone).valid)
      ) {
        alert(t("auth.phoneMustBeVerified"));
        return;
      }

      try {
        setIsLoading(true);
        const updateData: EditUserData = {
          nickname: formData.nickname,
          email: formData.email,
          fullname: formData.fullname,
          subscribed: formData.subscribed,
        };

        if ((isGuest || showPassword) && formData.password) {
          updateData.password = formData.password;
        }

        if (phoneVerification.verificationToken) {
          updateData.phone = phoneVerification.phone;
          updateData.phoneVerificationToken = phoneVerification.verificationToken;
        }

        const updatedUser = await editUser(updateData);
        onUserUpdate(updatedUser);
        setIsEditing(false);
        setShowPassword(false);
        setFormData((prev) => ({ ...prev, password: "", confirmPassword: "" }));
      } catch (error: any) {
        alert(error.message || t("errors.failedToUpdateUser"));
      } finally {
        setIsLoading(false);
      }
    }, [
      isDataChanged,
      isLoading,
      showPassword,
      formData,
      isGuest,
      phoneVerification,
      user?.phone,
      onUserUpdate,
      t,
    ]);

    const handleDelete = useCallback(async () => {
      if (deleteConfirmText !== user?.nickname) {
        alert(t("user.usernameDoesNotMatch"));
        return;
      }

      try {
        setIsLoading(true);
        await deleteSelf();
        onLogout();
      } catch (error: any) {
        alert(error.message || t("errors.failedToDeleteAccount"));
        setIsLoading(false);
      }
    }, [deleteConfirmText, user?.nickname, onLogout, t]);

    const [linkError, setLinkError] = useState<string | null>(null);

    const handleVkLink = useCallback(() => {
      setLinkError(null);
      startVkAuth({ mode: "link" }).catch((e: any) =>
        setLinkError(e?.message || t("auth.vkFailed"))
      );
    }, [t]);

    const showLinking = !IS_TELEGRAM && !!user && !isEditing;
    const showVkAccount = !!user?.vkUserId || isVkConfigured();

    if (!user) return null;

    return (
      <div className="user-panel-overlay" onClick={handleOverlayClick}>
        <div className="user-panel" onClick={handlePanelClick}>
          <div className="user-panel-header">
            <h3>{isEditing ? t("user.editProfile") : t("user.userProfile")}</h3>
            <div className="header-actions">
              {!isEditing && (
                <button
                  className="edit-button"
                  onClick={() => setIsEditing(true)}
                  title={t("user.editProfile")}
                  data-testid="user-edit-button"
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/images/edit_prod.png`}
                    alt="Edit"
                    className="edit-icon"
                  />
                </button>
              )}
              <button className="close-button" onClick={onClose}>
                ×
              </button>
            </div>
          </div>

          <div className="user-panel-content">
            <div className="user-avatar-section">
              <div
                className={`user-avatar-large ${isEditing ? "editable" : ""}`}
                onClick={handleAvatarClick}
              >
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt={`${user.nickname}'s avatar`}
                    className="avatar-image"
                  />
                ) : (
                  <div className="avatar-placeholder">{userInitial}</div>
                )}
                {isEditing && (
                  <div className="avatar-edit-overlay">
                    <img
                      src={`${process.env.PUBLIC_URL}/images/photo.png`}
                      alt="Camera"
                      className="camera-icon"
                    />
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleAvatarChange}
              />
              {isEditing && (
                <span className="edit-avatar-hint">{t("user.clickToChangeAvatar")}</span>
              )}
              {!isEditing && <h4 className="user-nickname">{user.nickname}</h4>}
            </div>

            {isEditing ? (
              <div className="user-edit-form">
                <div className="form-group">
                  <label>{t("auth.nickname")}</label>
                  <input
                    type="text"
                    value={formData.nickname}
                    onChange={(e) =>
                      handleInputChange("nickname", e.target.value)
                    }
                    placeholder={t("auth.nickname")}
                    maxLength={20}
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>{t("user.fullName")}</label>
                  <input
                    type="text"
                    value={formData.fullname}
                    onChange={(e) =>
                      handleInputChange("fullname", e.target.value)
                    }
                    placeholder={t("auth.fullNameOptional")}
                    maxLength={128}
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>{t("auth.email")}</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    placeholder={t("auth.email")}
                    maxLength={50}
                    disabled={isLoading}
                  />
                </div>

                {!isGuest && user.phone && (
                  <div className="form-group">
                    <label>{t("auth.phone")}</label>
                    <input type="tel" value={formatRussianPhone(user.phone)} disabled />
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => {
                        setShowPhoneEditor((visible) => !visible);
                        setPhoneVerification({
                          phone: user.phone || "+7",
                          verificationToken: null,
                        });
                      }}
                    >
                      {showPhoneEditor
                        ? t("user.cancelPhoneChange")
                        : t("user.changePhone")}
                    </button>
                  </div>
                )}

                {(isGuest || !user.phone || showPhoneEditor) && (
                  <div className="guest-phone-verification">
                    {isGuest && (
                      <p className="guest-phone-verification-intro">
                        {t("auth.guestPhoneRequired")}
                      </p>
                    )}
                    <PhoneVerificationField
                      disabled={isLoading}
                      initialPhone={showPhoneEditor ? user.phone || "+7" : "+7"}
                      onChange={setPhoneVerification}
                      required={isGuest}
                      testIdPrefix={isGuest ? "guest" : "profile"}
                    />
                  </div>
                )}

                {!isGuest && !showPassword ? (
                  <button
                    className="link-button"
                    onClick={() => setShowPassword(true)}
                  >
                    {t("user.changePassword")}
                  </button>
                ) : (
                  <>
                    <div className="form-group">
                      <label>
                        {isGuest ? t("auth.password") : t("user.newPassword")}
                      </label>
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) =>
                          handleInputChange("password", e.target.value)
                        }
                        placeholder={
                          isGuest ? t("auth.password") : t("user.newPassword")
                        }
                        maxLength={50}
                        disabled={isLoading}
                        required={isGuest}
                      />
                    </div>

                    <div className="form-group">
                      <label>{t("auth.confirmPassword")}</label>
                      <input
                        type="password"
                        value={formData.confirmPassword}
                        onChange={(e) =>
                          handleInputChange("confirmPassword", e.target.value)
                        }
                        placeholder={t("auth.confirmPassword")}
                        maxLength={50}
                        disabled={isLoading}
                        required={isGuest}
                      />
                    </div>

                    {!isGuest && (
                      <button
                        className="link-button"
                        onClick={() => {
                          setShowPassword(false);
                          setFormData((prev) => ({
                            ...prev,
                            password: "",
                            confirmPassword: "",
                          }));
                        }}
                      >
                        {t("common.cancel")}
                      </button>
                    )}
                  </>
                )}

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.subscribed}
                      onChange={(e) =>
                        handleInputChange("subscribed", e.target.checked)
                      }
                      disabled={isLoading}
                    />
                    {t("user.subscribeToNewsletter")}
                  </label>
                </div>

                <div className="form-actions">
                  <button
                    className="cancel-button"
                    onClick={() => {
                      setIsEditing(false);
                      setShowPassword(false);
                    }}
                    disabled={isLoading}
                  >
                    {t("common.cancel")}
                  </button>
                  <button
                    className={`submit-button ${
                      isDataChanged ? "active" : "disabled"
                    }`}
                    onClick={handleSubmit}
                    disabled={!isDataChanged || isLoading}
                    data-testid="user-save-button"
                  >
                    {isLoading ? t("user.saving") : t("user.saveChanges")}
                  </button>
                </div>

                <div className="danger-zone">
                  <button
                    className="logout-button-secondary"
                    onClick={onLogout}
                    disabled={isLoading}
                  >
                    {t("auth.logout")}
                  </button>

                  {!showDeleteConfirm ? (
                    <button
                      className="delete-button"
                      onClick={() => setShowDeleteConfirm(true)}
                      disabled={isLoading}
                    >
                      {t("user.deleteAccount")}
                    </button>
                  ) : (
                    <div className="delete-confirm">
                      <p className="delete-warning">
                        {t("user.typeUsernameToConfirm")}
                      </p>
                      <input
                        type="text"
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        placeholder={user.nickname}
                        disabled={isLoading}
                      />
                      <div className="delete-confirm-actions">
                        <button
                          className="cancel-button"
                          onClick={() => {
                            setShowDeleteConfirm(false);
                            setDeleteConfirmText("");
                          }}
                          disabled={isLoading}
                        >
                          {t("common.cancel")}
                        </button>
                        <button
                          className="delete-button"
                          onClick={handleDelete}
                          disabled={
                            deleteConfirmText !== user.nickname || isLoading
                          }
                        >
                          {isLoading ? t("user.deleting") : t("user.deleteForever")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="user-details">
                  {user.fullname && (
                    <div className="detail-item">
                      <span className="detail-label">{t("user.fullName")}:</span>
                      <span className="detail-value">{user.fullname}</span>
                    </div>
                  )}

                  <div className="detail-item">
                    <span className="detail-label">{t("auth.email")}:</span>
                    <span className="detail-value">{user.email}</span>
                  </div>

                  <div className="detail-item">
                    <span className="detail-label">{t("user.points")}:</span>
                    <span className="detail-value coins">
                      {user.coins}
                      <img
                        src={`${process.env.PUBLIC_URL}/images/coin.png`}
                        alt="Coins"
                        className="coin-icon"
                      />
                    </span>
                  </div>

                  {user.telegramUserId && (
                    <div className="detail-item telegram-linked-row">
                      <span className="detail-label">{t("user.telegram")}:</span>
                      <span className="detail-value">{t("user.linked")}</span>
                    </div>
                  )}
                </div>

                {showLinking && showVkAccount && (
                  <div className="linked-accounts">
                    <h5 className="linked-accounts-title">
                      {t("user.linkedAccounts")}
                    </h5>
                    {linkError && <div className="auth-error">{linkError}</div>}

                    <div className="linked-account-row">
                      <div className="linked-account-info">
                        <img
                          className="linked-account-icon"
                          src={`${process.env.PUBLIC_URL}/images/vk_logo.png`}
                          alt=""
                          aria-hidden="true"
                        />
                        <span className="linked-account-name">
                          {t("user.vk")}
                        </span>
                      </div>
                      {user.vkUserId ? (
                        <span className="linked-badge" data-testid="vk-linked">
                          <LinkedCheck />
                          {t("user.linked")}
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="linked-link-btn"
                          data-testid="vk-link-btn"
                          onClick={handleVkLink}
                        >
                          {t("user.link")}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                <div className="user-actions">
                  <button className="logout-button" onClick={onLogout}>
                    {t("auth.logout")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
);

UserPanel.displayName = "UserPanel";

export default UserPanel;
